import { MessageBar, MessageBarType, Separator, Spinner, SpinnerSize } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { LwaClassification, LwaImpact, LwaOutcomeRisk, LwaPersonalDataScope, LwaSolutionScope } from '../../../GlobalConst/globalEnums';
import { mapEnumToOptions, mapEnumToOptionsWithValues } from '../../../helpers/HelperMethods';
import CustomDropDown from '../SiteEditElements/CustomDropDown';
import CustomTextField from '../SiteEditElements/CustomTextField';
import SecuredComponent from '../SiteEditElements/SecuredComponent';
import TabChangedDialog from '../TabChangedDialog';
import { LightWeightAssessmentQA } from './LightWeightAssessmentQA';
import Api from '../../../services/Api';

const LightWeightAssessment = ({ solution, disabled, onUpdate, isEditEnabled, isSaveEnabled, btnEdit, btnSave, messagePanel, showTabSwitchDialog, onDiscardDialog, onSaveDialog, onCancelDialog, getValue, onChildrenValidation}) => {

    const currentUser = useSelector((state) => state.user.currentUser)
    const myOwnSolution = [solution.primaryOwner, solution.secondaryOwner].some(x => x && x.userId === currentUser.id) || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner

    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true)
    const [lwaValue, setLwaValue] = useState(getValue('lightWeightAssessment') ? getValue('lightWeightAssessment') : solution.lightWeightAssessment)
    const [isCmdbIdValidating, setIsCmdbIdValidating] = useState(false) 
    const [isCmdbIdAssessed, setIsCmdbIdAssessed] = useState(solution.lightWeightAssessment && solution.lightWeightAssessment.infoSecValidationStatus === 'Completed')

    useEffect(() => {
        const isValid = Object.keys(childrenValidation).reduce((value, key) => {
            return value && childrenValidation[key];
        }, true)

        setIsChildrenUpdateValid(isValid)
        if (onChildrenValidation)
            onChildrenValidation(isValid)
    }, [childrenValidation])

    useEffect(() => {
        if (lwaValue) {
            onUpdate('lightWeightAssessment', lwaValue)
        }
    }, [lwaValue])
    
    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const onFieldUpdate = (fieldName, value) => {
        const result = {
                ...lwaValue,
                [fieldName]: value
            }
        
        const outcomeRisk = calculateOutcomeRisk(result)

        setLwaValue((prevSet) => {
            return {
                ...result,
                outcomeRisk
            }
        })
    }

    const calculateOutcomeRisk = (values) => {
        if (values.impact === LwaImpact.high 
            || values.classification === LwaClassification.strictlyConfidential) {
            return LwaOutcomeRisk.high;
        }

        if (values.vendor === true
            || values.otherApplicationsConnected === true
            || values.militaryData === true
            || values.financialData === true
            || values.personalData === LwaPersonalDataScope.personalData) {
            return LwaOutcomeRisk.medium;
        }

        if (values.scope === LwaSolutionScope.businessPurposes) {
            return LwaOutcomeRisk.low;
        }

        return LwaOutcomeRisk.personalProductivity;
    }

    const getOutcomeRiskMessage = (outcomeRisk) => {
        switch (outcomeRisk) {
            case LwaOutcomeRisk.personalProductivity:
                return <span>Based your assessment, this solution is classified as <b>personal productivity</b> type.No action  required. <br />Please see here <a href="https://abb.sharepoint.com/sites/powerplatformhub/SitePages/Recommendations-for-Low-Risk-Business-Solutions.aspx?source=https%3A%2F%2Fabb.sharepoint.com%2Fsites%2Fpowerplatformhub%2FSitePages%2FForms%2FByAuthor.aspx%3Fas%3Djson%26FolderCTID%3D0x0120007F5897DA974D25469E1120DC020D8B78%26viewid%3D637b8f65%252Dfc62%252D4af3%252D8771%252Dd161ad4d11a8">InfoSec recommendations.</a></span>
            case LwaOutcomeRisk.low:
                return <span>Based your assessment, this solution is classified as <b>low risk</b>.<br/>You will receive recommendations in email from Infosec.</span>
            case LwaOutcomeRisk.medium:
            case LwaOutcomeRisk.high:
                return <span>Based your assessment, this solution is classified as <b>business critical</b>.<br/>You will receive next steps in email from Infosec.</span>
            default:
                return null
        }
    }

    // const isCmdbRequired = () => {
    //     return lwaValue && (lwaValue.outcomeRisk === LwaOutcomeRisk.medium || lwaValue.outcomeRisk === LwaOutcomeRisk.high)
    // }

    const getCmdbMessage = () => {
        return lwaValue && (lwaValue.outcomeRisk === LwaOutcomeRisk.medium || lwaValue.outcomeRisk === LwaOutcomeRisk.high) && !getValue('cmdbId') &&
            <span>CMDB ID is required for  business-critical solutions. If you have not registered yet, you can do that by following link and instructions provided in email InfoSec. Ensure you have selected "Power Platform" under "Platform" field while registering application in ServiceNow catalog.</span>
    }

    const getScopeErrorMessage = () => {
        if (lwaValue && lwaValue.outcomeRisk !== LwaOutcomeRisk.personalProductivity && lwaValue.scope === LwaSolutionScope.personalProductivity)
            return 'You have selected your solution type is Personal Productivity but based on the other question responses your solution is a business solution. Kindly confirm the solution purpose if its for "Business Purposes"'
    }

    const validateCmdbAssessment = async (cmdbId) => {
        if (!cmdbId) {
            setIsCmdbIdAssessed(false)
            
            const outcomeRisk = calculateOutcomeRisk(lwaValue)
            setLwaValue((prevSet) => {
                return {
                    ...lwaValue,
                    outcomeRisk
                }
            })
            return
        }

        setIsCmdbIdValidating(true)

        const result = await Api.get(`/EntryValidation/ValidateCmdbAssessment?cmdbId=${cmdbId}`)

        if (result && result.data) {
            onFieldUpdate('scope', LwaSolutionScope.businessPurposes)
            setIsCmdbIdAssessed(true)

            setLwaValue((prevSet) => {
                return {
                    ...prevSet,
                    outcomeRisk: null
                }
        })
        }
        else {
            setIsCmdbIdAssessed(false)
        }
        
        setIsCmdbIdValidating(false)
    }

    const validatingSpinner = <div title='Validating...' className='validation-spinner-noLabel'><Spinner size={SpinnerSize.medium} /></div>

    const editor =
        <div className="site-editor">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <SecuredComponent
                    readSecurityLevel='Reader'
                    editSecurityLevel='Contributor'
                    unlimitedAccess={myOwnSolution}
                >
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.scope.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.scope.answers)}
                                value={(lwaValue || {}).scope}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('scope', v)}
                                required={true}
                                onValidation={(v) => onUpdateChildrenValidation('scope', v)}
                                errorMessage={getScopeErrorMessage()}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>Have you registered your application in CMDB? If Yes – please enter the CMDB ID here. (Reference <a href="https://abbnow.service-now.com/cs_gr?id=esc_sc_cat_item&sys_id=a99da47487ebd994dc10eb580cbb35ae">here</a> to create a new business application)</label>
                        </div>
                        <div style={{...twoColumnsSingleCol, position: 'relative'}}>
                            <CustomTextField
                                defaultValue={getValue('cmdbId')}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('cmdbId', v)}
                                required={false}
                                onValidation={(v) => onUpdateChildrenValidation('cmdbId', v)}
                                warningMessage={getCmdbMessage()}
                                onBlur={() => validateCmdbAssessment(getValue('cmdbId'))}
                            />
                            {
                                isCmdbIdValidating && validatingSpinner
                            }
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.personalData.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.personalData.answers)}
                                defaultValue={(lwaValue || {}).personalData}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('personalData', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('personalData', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.financialData.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.financialData.answers)}
                                defaultValue={(lwaValue || {}).financialData}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('financialData', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('financialData', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.militaryData.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.militaryData.answers)}
                                defaultValue={(lwaValue || {}).militaryData}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('militaryData', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('militaryData', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.otherApplicationsConnected.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.otherApplicationsConnected.answers)}
                                defaultValue={(lwaValue || {}).otherApplicationsConnected}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('otherApplicationsConnected', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('otherApplicationsConnected', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.classification.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptions(LightWeightAssessmentQA.classification.answers)}
                                defaultValue={(lwaValue || {}).classification}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('classification', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('classification', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.impact.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.impact.answers)}
                                defaultValue={(lwaValue || {}).impact}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('impact', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('impact', v)}
                            />
                        </div>
                    </div>
                    <div style={twoColumnsContainer}>
                        <div style={twoColumnsSingleColLabel}>
                            <label className='item-title'>{LightWeightAssessmentQA.vendor.question}</label>
                        </div>
                        <div style={twoColumnsSingleCol}>
                            <CustomDropDown
                                options={mapEnumToOptionsWithValues(LightWeightAssessmentQA.vendor.answers)}
                                defaultValue={(lwaValue || {}).vendor}
                                disabled={disabled || isCmdbIdAssessed}
                                onUpdate={(v) => onFieldUpdate('vendor', v)}
                                required={!isCmdbIdAssessed}
                                onValidation={(v) => onUpdateChildrenValidation('vendor', v)}
                            />
                        </div>
                    </div>
                </SecuredComponent>
            </div>
        </div>

    const editPanel = 
        <div>
            <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', padding: '0 20px'}}>
                <SecuredComponent readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnSolution}>
                    <div style={{ width: '100%', marginTop: '20px', display: "flex", justifyContent: "space-between"}}>
                        {isEditEnabled() && btnEdit}
                        {!disabled && btnSave(isSaveEnabled() && (isChildrenUpdateValid || isCmdbIdAssessed))}
                    </div>
                </SecuredComponent>
            </div>
            {messagePanel}
        </div>

    const outcomeRiskMessage = lwaValue && lwaValue.outcomeRisk && !isCmdbIdAssessed &&
        <MessageBar
            dismissButtonAriaLabel="Close"
            truncated={true}
            messageBarType={MessageBarType.warning}>
            {getOutcomeRiskMessage(lwaValue.outcomeRisk)}
        </MessageBar>

    const isCmdbIdAssessedMessage = isCmdbIdAssessed &&
        <MessageBar
            dismissButtonAriaLabel="Close"
            truncated={true}
            messageBarType={MessageBarType.success}>
            Infosec Assessment status – Completed
        </MessageBar>

    const warning = 
        <MessageBar
            dismissButtonAriaLabel="Close"
            truncated={true}
            messageBarType={MessageBarType.info}
            styles={{root: {width: 650}}}>
            Please note: This risk profile identification is only local assessments. Meaning – these are not synchronized to the CMDB or CESAR. This list of questions will help you to identify the risk exposure of your solutions and next actions for business-critical solutions.
        </MessageBar>
            
    return (
        <div>
            {warning}
            {editor}
            <Separator />
            {outcomeRiskMessage}
            {isCmdbIdAssessedMessage}
            <Separator />
            {editPanel}
            <TabChangedDialog
                isSaveEnabled={isSaveEnabled() && isChildrenUpdateValid}
                showDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={onCancelDialog}
            />
        </div>
    )
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
    alignItems: 'center',
}

const twoColumnsSingleCol = {
    flex: 2,
    overflow: 'hidden'
}

const twoColumnsSingleColLabel = {
    flex: 3,
    overflow: 'hidden'
}

export default LightWeightAssessment;


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditElements/LightWeightAssessment.js